/**
 * Shared constants for the backtesting engine.
 * Using named constants avoids magic numbers scattered across files
 * and makes it easy to update values in one place.
 */

// ============================================================================
// Warmup
// ============================================================================

/**
 * Number of candle bars to prepend before startDate so strategies have
 * indicator history before trading begins.
 *
 * Both the plain aggregate path (local-aggregate.ts) and the grid-search /
 * walk-forward path (local-aggregation.ts / getPrecomputed) MUST use this
 * same constant so the two paths can never drift again.
 *
 * Value of 200 covers the longest common indicator lookbacks (e.g. SMA-200,
 * Bollinger Bands with 200-bar lookback, Squeeze Momentum with 100-bar lookback).
 */
export const WARMUP_BARS = 200;

/**
 * B1 fix (BT<->PT parity bug): upper bound on a strategy's declared
 * `warmupBars` (see strategy/warmup-declaration.ts and Strategy.warmupBars in
 * strategy/base.ts). Bounds both the backtest warmup-prepend size and
 * paper-trading's provisioned candle window so a misconfigured/malicious
 * declaration cannot request an unbounded amount of history (Rule 11: an
 * out-of-range value throws at resolution time rather than being silently
 * clamped).
 */
export const MAX_STRATEGY_WARMUP_BARS = 2000;

// ============================================================================
// Fee Rates
// ============================================================================

/**
 * Conservative default taker fee rate (0.1%).
 * Used in the single-asset engine when no exchange fee can be fetched.
 * This is intentionally conservative to avoid over-estimating performance.
 */
export const DEFAULT_TAKER_FEE_RATE = 0.001;

/**
 * Bybit's actual taker fee rate (0.055%).
 * Used in the aggregate engine and paper trading engine which target Bybit.
 */
export const DEFAULT_BYBIT_TAKER_FEE_RATE = 0.00055;

// ============================================================================
// Slippage
// ============================================================================

/**
 * Default slippage percentage applied to futures trades (0.05%).
 * Applied at entry and exit to simulate market impact in futures mode.
 */
export const DEFAULT_FUTURES_SLIPPAGE_PERCENT = 0.05;

/**
 * Resolve the effective slippage percentage for a given trading mode.
 *
 * Rules (Rule 11 — no silent fallback):
 *   - When `slippagePercent` is explicitly provided (including 0), use it as-is.
 *   - When omitted (undefined), apply the mode-aware default:
 *       futures → DEFAULT_FUTURES_SLIPPAGE_PERCENT (0.05%)
 *       spot    → 0  (spot fills are at the book; no simulated spread)
 *   - Live-trading (LT) uses real exchange slippage, NOT this helper.
 *   - This helper is for BT/GS/WF entry points ONLY (`aggregate-grid-search.ts`,
 *     `aggregate-walk-forward.ts`, `useClientOptimization.ts`, `backtest.worker.ts`,
 *     and the `mcp-server/src/local-*.ts` entry points).
 *
 *     Paper-trading does NOT use this helper. Its fill path
 *     (`src/api/routes/paper-trading.ts` session creation, consumed by
 *     `session-manager.ts` `buildConnectorConfig`) defaults an omitted
 *     `slippagePercent` to `DEFAULT_FUTURES_SLIPPAGE_PERCENT` (0.05%) for BOTH
 *     spot and futures mode — this is deliberate and must NOT be "fixed" to call
 *     this mode-aware helper. A SHA-28 regression briefly wired paper-trading
 *     through this helper, which silently zeroed spot-mode paper fill slippage
 *     (0% instead of the pre-existing 0.05%) and made spot paper results too
 *     optimistic; it was reverted to the explicit
 *     `parsed.slippagePercent ?? DEFAULT_FUTURES_SLIPPAGE_PERCENT` default (SHA-28
 *     follow-up fix). An explicit user-supplied `slippagePercent` is still always
 *     honored for paper sessions, in both modes.
 *
 * All BT/GS/WF entry points MUST call this helper instead of `?? 0` so there is
 * a single definition of the default and the fix can never drift.
 *
 * @param mode            'futures' | 'spot' | undefined  (undefined defaults to 'futures')
 * @param slippagePercent Caller-supplied value, or undefined to use the mode-aware default.
 */
export function resolveSlippagePercent(
  mode: 'futures' | 'spot' | undefined,
  slippagePercent: number | undefined,
): number {
  if (slippagePercent !== undefined) return slippagePercent;
  const effectiveMode = mode ?? 'futures';
  return effectiveMode === 'futures' ? DEFAULT_FUTURES_SLIPPAGE_PERCENT : 0;
}

/**
 * SHA-28: empirical mean-adverse intrabar-fill deviation, in basis points,
 * observed when comparing modelled backtest fills against real Bybit fills
 * on volatile 4h bars. Aligned-bar fills agreed <0.25% of the time; the
 * remainder showed +-5-7% single-fill deviations driven by intrabar price
 * travel (NOT spread).
 *
 * This is NOT a slippage-cost model input (see `resolveSlippagePercent` /
 * `DEFAULT_FUTURES_SLIPPAGE_PERCENT` for that). It is the basis for a
 * separate, ADDITIVE +- uncertainty band reported alongside point-estimate
 * results (see `calculateSlippageUncertaintyBandPercent` in
 * `src/analysis/metrics.ts`) so users can see how much a real fill could
 * plausibly have deviated from the model. It must NEVER be used to adjust
 * any point-estimate metric.
 */
export const SLIPPAGE_UNCERTAINTY_BPS_PER_FILL = 38;

// ============================================================================
// Metrics
// ============================================================================

/**
 * Cap for Sortino ratio when there are no negative returns (prevents infinity).
 */
export const SORTINO_CAP = 10;

/**
 * Rolling window size (in bars) used when computing rolling Sharpe ratio.
 */
export const ROLLING_SHARPE_WINDOW = 50;

/**
 * Cap for profit factor when gross loss is zero (prevents infinity).
 */
export const PROFIT_FACTOR_CAP = 10;

/**
 * Minimum magnitude (in % points) for both Sharpe and compound return to be considered
 * "non-trivial" when checking for a sign divergence between them.
 *
 * A loss smaller than this threshold is considered noise and will NOT trigger
 * `sharpeReturnSignMismatch` even if the Sharpe is positive (or vice-versa).
 *
 * Unit: percentage points (e.g. 1.0 = 1%).
 */
export const SHARPE_RETURN_MISMATCH_LOSS_EPS = 1.0;

/**
 * Minimum magnitude for the Sharpe ratio to be considered "non-trivial"
 * when checking for a sign divergence between Sharpe and compound return.
 *
 * A Sharpe smaller than this threshold will NOT trigger `sharpeReturnSignMismatch`.
 */
export const SHARPE_RETURN_MISMATCH_SHARPE_EPS = 0.01;

// ============================================================================
// Grid Search / Walk-Forward combination limits (SHA-9)
//
// Named so the "sane maximum" enforced server-side is documented and can't
// silently drift between the MCP tool zod schemas and the engine defaults.
// See also GRID_SEARCH_MAX_RAW_COMBINATIONS in grid-search-core.ts, which
// bounds the PRE-sampling raw cartesian product size (a distinct, deeper
// safeguard against param_ranges with huge per-parameter value arrays).
// ============================================================================

/**
 * Default max_combinations for aggregate grid search when the caller omits
 * it. NOT "unlimited" — every aggregate grid search is bounded even when the
 * request doesn't specify a cap.
 */
export const GRID_SEARCH_DEFAULT_MAX_COMBINATIONS = 500;

/**
 * Hard cap on the max_combinations REQUEST value accepted by the MCP grid
 * search tools (run_grid_search / submit_grid_search). Requests above this
 * are rejected loudly by the zod schema (no silent clamping).
 */
export const GRID_SEARCH_MAX_COMBINATIONS_REQUEST_CAP = 10_000;

/**
 * Default max_combinations PER WINDOW for aggregate walk-forward when the
 * caller omits it. Deliberately lower than GRID_SEARCH_DEFAULT_MAX_COMBINATIONS
 * because walk-forward runs num_windows (up to 20) independent grid searches,
 * so the total backtest count is this value × num_windows.
 */
export const WALK_FORWARD_DEFAULT_MAX_COMBINATIONS_PER_WINDOW = 200;

/**
 * Hard cap on the max_combinations REQUEST value accepted by the (sync,
 * stdio-only) run_walk_forward tool. Lower than the plain grid-search request
 * cap for the same reason as the default above (per-window × num_windows).
 */
export const WALK_FORWARD_MAX_COMBINATIONS_REQUEST_CAP = 500;
